import { IconButton } from '@fluentui/react';
import React from 'react';
import { RagColours } from '../../refData/RagColours';
import { DateService } from '../../services';
import { PreviousRagIndicator } from './PreviousRagIndicator';
import { RagIndicator } from './RagIndicator';
import { ReviewListProgressUpdate } from './ReviewListProgressUpdate';
import styles from '../../styles/cr.module.scss';
import { WorkflowType } from '../../refData/WorkflowType';
import { impactTitles, probabilityTitles } from '../RiskReportingAdmin';

export const renderEditButton = (onEdit: (entityId: number, entityUpdateId: number) => void, entityId: number, entityUpdateId: number, showEditButton: boolean): JSX.Element => {
    return showEditButton &&
        <IconButton iconProps={{ iconName: 'Edit' }} title='Edit' onClick={() => onEdit(entityId, entityUpdateId)} />;
}

export const renderProgressUpdate = ({ title, comment, author, date, toBeClosed, customFields, previousComment }
    : { title: string, comment: string, author: string, date: Date, toBeClosed: boolean, customFields?: string[], previousComment?: string }): JSX.Element => {
    return (
        <ReviewListProgressUpdate
            entityName={title}
            progressUpdate={comment}
            updateAuthor={author}
            updateDate={date}
            toBeClosed={toBeClosed}
            customFields={customFields}
            previousUpdate={previousComment}
        />
    );
}

export const renderProgressUpdateWithMobileSupport = ({
    title,
    comment,
    author,
    date,
    toBeClosed,
    customFields,
    previousComment,
    lead,
    rag,
    isMobile,
    additionalItemsString
}: {
    title: string;
    comment: string;
    author: string;
    date: Date;
    toBeClosed: boolean;
    customFields?: string[];
    previousComment?: string;
    lead?: string;
    rag?: RagColours;
    isMobile?: boolean;
    additionalItemsString?: string;
}): JSX.Element => {
    return (
        <div>
            {/* Show rag on top only for mobile */}
            {isMobile && (
                <div style={{ marginBottom: '4px', maxWidth: '100px' }}>
                    {renderRag(rag)}
                </div>
            )}

            <ReviewListProgressUpdate
                entityName={title}
                progressUpdate={comment}
                updateAuthor={author}
                updateDate={date}
                toBeClosed={toBeClosed}
                customFields={customFields}
                previousUpdate={previousComment}
                lead={lead}
                additionalItemsString={additionalItemsString}
            />
        </div>
    );
};


export const renderDate = (date: Date | string, noDateText?: string): JSX.Element => {
    const parsedDate = date instanceof Date ? date : typeof date === 'string' ? new Date(date) : null;

    if (!isValidDate(parsedDate)) {
        return <span>{noDateText || 'N/A'}</span>;
    }

    return (
        <span>
            {DateService.dateToUkDate(parsedDate)}
        </span>
    );
};

export const renderDateWithTime = (date: Date | string, noDateText?: string): JSX.Element => {
    const parsedDate = date instanceof Date ? date : typeof date === 'string' ? new Date(date) : null;

    if (!isValidDate(parsedDate)) {
        return <span>{noDateText || 'N/A'}</span>;
    }

    return (
        <span>
            {DateService.dateToUkDateTime(parsedDate)}
        </span>
    );
};

export const isValidDate = (date: Date | null): date is Date => {
    return date instanceof Date && !isNaN(date.getTime());
};


export const renderRag = (rag: RagColours): JSX.Element => <RagIndicator rag={rag} />

export const renderPreviousRag = (rag: RagColours): JSX.Element => <PreviousRagIndicator rag={rag} />

export const renderPortfolioIcons = (purpose: string, containsValue?: string): JSX.Element => {
    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const portfolioGeneralManagement: string = `${imagesRoot}portfolio-general-management.png`;
    const portfolioVisionRealisation: string = `${imagesRoot}portfolio-vision-realisation.png`;

    const portfolioContainsProjects: string = `${imagesRoot}portfolio-contains-projects.png`;
    const portfolioContainsPortfolios: string = `${imagesRoot}portfolio-contains-portfolios.png`;

    let portfolioPurposeIcon: string = '';
    let portfolioPurposeAlt: string = '';

    if (purpose === 'Vision realisation') {
        portfolioPurposeIcon = portfolioVisionRealisation;
        portfolioPurposeAlt = 'Vision realisation';
    }
    else {
        portfolioPurposeIcon = portfolioGeneralManagement;
        portfolioPurposeAlt = 'General Management';
    }

    let portfolioContainsIcon: string = '';
    let portfolioContainsAlt: string = '';

    if (containsValue === 'Portfolios') {
        portfolioContainsIcon = portfolioContainsPortfolios;
        portfolioContainsAlt = 'Portfolios';
    }
    else {
        portfolioContainsIcon = portfolioContainsProjects;
        portfolioContainsAlt = 'Programmes & Projects';
    }


    return (
        <div className={styles.indicatorsContainer}>
            <div className={styles.portfolioIconBox}><img src={portfolioPurposeIcon} alt={portfolioPurposeAlt} title={portfolioPurposeAlt} /></div>
            {containsValue && <div className={styles.portfolioIconBox}><img src={portfolioContainsIcon} alt={portfolioContainsAlt} title={portfolioContainsAlt} /></div>}
        </div>
    );
};

const indicatorCssMap = new Map<number, string>([
    [5, styles.darkGreenIndicator],
    [4, styles.greenIndicator],
    [3, styles.amberIndicator],
    [2, styles.redIndicator],
    [1, styles.darkRedIndicator],
]);

const getIndicatorInfo = (ratingFor: string, indicatorID: number): { cssClass: string, title: string } => {
    const cssClass = indicatorCssMap.get(indicatorID) || '';
    let title = `${ratingFor}: Undefined rating`;

    switch (indicatorID) {
        case 5:
            title = `${ratingFor}: Dark Green\nOn track – no major issues`;
            break;
        case 4:
            title = `${ratingFor}: Green\nGenerally on track – minor issues only`;
            break;
        case 3:
            title = `${ratingFor}: Amber\nSome concerns – requires attention`;
            break;
        case 2:
            title = `${ratingFor}: Red\nSevere concerns – major risks present`;
            break;
        case 1:
            title = `${ratingFor}: Dark Red\nCritical issues – outcome not achievable`;
            break;
    }

    return { cssClass, title };
};

export const renderIndicators = (
    overallRagOptionID: number,
    financeRagOptionID: number,
    peopleRagOptionID: number,
    milestonesRagOptionID: number,
    benefitsRagOptionID: number
): JSX.Element => {
    const indicatorsInfo = [
        getIndicatorInfo('Delivery confidence', overallRagOptionID),
        getIndicatorInfo('Finance', financeRagOptionID),
        getIndicatorInfo('People', peopleRagOptionID),
        getIndicatorInfo('Milestones', milestonesRagOptionID),
        getIndicatorInfo('Benefits', benefitsRagOptionID),
    ];

    return (
        <div className={styles.indicatorsContainer}>
            {indicatorsInfo.map((info, index) => (
                <div
                    key={index}
                    className={`${styles.indicatorBox} ${info.cssClass}`}
                    title={info.title}
                ></div>
            ))}
        </div>
    );
};





export const renderRiskIndicators = (
    impactOrProbability: string,
    showTargetTick: boolean,
    showUnmitigatedTick: boolean,
    UnmitigatedRiskProbabilityID: number,
    UnmitigatedRiskImpactLevelID: number,
    TargetRiskProbabilityID: number,
    TargetRiskImpactLevelID: number
): JSX.Element => {
    const riskColors: Record<number, string> = {
        1: "#006837", // green
        2: "#87CB66", // light-green
        3: "#FFCC00", //amber-yellow  //"#FEFEBE", // yellow
        4: "#F88B52", // orange
        5: "#A50126", // red
    };

    // Decide which IDs & lookup map to use
    const isImpact = impactOrProbability === "Impact";
    const [targetId, unmitigatedId] = isImpact
        ? [TargetRiskImpactLevelID, UnmitigatedRiskImpactLevelID]
        : [TargetRiskProbabilityID, UnmitigatedRiskProbabilityID];

    const lookup = isImpact ? impactTitles : probabilityTitles;

    // Decide tick color by ID
    const getTickColor = (id: number) =>
        id === 1 || id === 5 ? "white" : "black";

    return (
        <div className={styles.indicatorsContainer}>
            <div
                className={`${styles.riskIndicatorBox} ${showTargetTick ? styles.ticked : ""}`}
                style={{
                    backgroundColor: riskColors[targetId] || "gray",
                    color: getTickColor(targetId),
                }}
                title={`Target ${impactOrProbability} ${lookup[targetId] || "Not Set"}`}
            ></div>
            <div
                className={`${styles.riskIndicatorBox} ${showUnmitigatedTick ? styles.ticked : ""}`}
                style={{
                    backgroundColor: riskColors[unmitigatedId] || "gray",
                    color: getTickColor(unmitigatedId),
                }}
                title={`Unmitigated ${impactOrProbability} ${lookup[unmitigatedId] || "Not Set"}`}
            ></div>
        </div>
    );
};




export const renderWorkflowIcon = (workflowTypeID): JSX.Element => {
    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const generalWorkflow: string = `${imagesRoot}general-workflow.png`;
    const linkedWorkflow: string = `${imagesRoot}linked-workflow.png`;
    const studyPlannerWorkflow: string = `${imagesRoot}study-planner-workflow.png`;


    let wfIcon: string = '';
    let wfIconAlt: string = '';

    if (workflowTypeID === WorkflowType.General) {
        wfIcon = generalWorkflow;
        wfIconAlt = 'General Work stream';
    }
    else if (workflowTypeID === WorkflowType.StudyPlanner) {
        wfIcon = studyPlannerWorkflow;
        wfIconAlt = 'Study Planner Work stream';
    }
    else {
        wfIcon = linkedWorkflow;
        wfIconAlt = 'Project Work stream';
    }

    return (
        <div className={styles.indicatorsContainer}>
            <div className={styles.portfolioIconBox}><img src={wfIcon} alt={wfIconAlt} title={wfIconAlt} /></div>
        </div>
    );
};

export const renderStage = (stage: string, stageColor: string): JSX.Element => {
    if (stage == null || stage == undefined)
        return null;

    // let stageClassName: string;

    // switch (stage) {
    //     case 'Awaiting':
    //         stageClassName = styles.stageAwaiting;
    //         break;
    //     case 'InProgress':
    //         stageClassName = styles.stageInProgress;
    //         break;
    //     case 'Completed':
    //         stageClassName = styles.stageCompleted;
    //         break;
    //     default:
    //         stageClassName = '';
    //         break;
    // }

    return (
        <div className={styles.stageContainer}>
            <div className={styles.stageText}>{stage}</div>
            <div className={`${styles.stageBottomBar}`} style={{ backgroundColor: stageColor }} ></div>
            {/* <div className={`${styles.stageBottomBar} ${stageClassName}`}></div> */}
        </div>
    );
};

export const renderDateRange = (startDate: Date | string, endDate: Date | string): JSX.Element => {
    const parsedStartDate = startDate instanceof Date ? startDate : typeof startDate === 'string' ? new Date(startDate) : null;
    const parsedEndDate = endDate instanceof Date ? endDate : typeof endDate === 'string' ? new Date(endDate) : null;

    if (!parsedStartDate && !parsedEndDate) return null;

    const startDateStr: string = parsedStartDate ? DateService.dateToUkShortDate(parsedStartDate) : '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
    const endDateStr: string = parsedEndDate ? DateService.dateToUkShortDate(parsedEndDate) : '';

    let blueBars = 0;
    let grayBars = 10;

    if (parsedStartDate && parsedEndDate) {
        const today = new Date();
        const workingHoursTotal = calculateWorkingHours(parsedStartDate, parsedEndDate);
        const workingHoursPassed = calculateWorkingHours(parsedStartDate, today);

        blueBars = Math.min(10, Math.round((workingHoursPassed / workingHoursTotal) * 10));
        grayBars = 10 - blueBars;
    }

    return (
        <div className={styles.dateRangeContainer}>
            <div className={styles.dateRangeText}>{startDateStr} - {endDateStr}</div>
            <div className={styles.dateRangeBarsContainer}>
                {[...Array(blueBars)].map((_, i) => <div key={`blue-${i}`} className={styles.dateRangeSmallBlueBar}></div>)}
                {[...Array(grayBars)].map((_, i) => <div key={`gray-${i}`} className={styles.dateRangeSmallGrayBar}></div>)}
            </div>
        </div>
    );
};

export const renderDateRangeSimple = (
    startDate: Date | string,
    endDate: Date | string
): JSX.Element => {
    const parsedStartDate =
        startDate instanceof Date
            ? startDate
            : typeof startDate === "string"
                ? new Date(startDate)
                : null;

    const parsedEndDate =
        endDate instanceof Date
            ? endDate
            : typeof endDate === "string"
                ? new Date(endDate)
                : null;

    if (!parsedStartDate && !parsedEndDate) {
        return null;
    }

    const startDateStr = parsedStartDate
        ? DateService.dateToUkShortDate(parsedStartDate)
        : "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0";

    const endDateStr = parsedEndDate
        ? DateService.dateToUkShortDate(parsedEndDate)
        : "";

    return (
        <div className={styles.dateRangeContainer}>
            <div className={styles.dateRangeText}>
                {startDateStr} - {endDateStr}
            </div>
        </div>
    );
};

const calculateWorkingHours = (start: Date, end: Date): number => {
    let totalHours = 0;
    let currentDate = new Date(start);

    while (currentDate <= end) {
        const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            totalHours += 8; // Only count weekdays
        }
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return totalHours;
};


export const renderDateRange_old = (startDate: Date | string, endDate: Date | string): JSX.Element => {
    const parsedStartDate = startDate instanceof Date ? startDate : typeof startDate === 'string' ? new Date(startDate) : null;
    const parsedEndtDate = endDate instanceof Date ? endDate : typeof endDate === 'string' ? new Date(endDate) : null;
    if (parsedStartDate === null && parsedEndtDate === null)
        return null;

    const startDateStr: string = parsedStartDate ? DateService.dateToUkShortDate(parsedStartDate) : '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'
    const endDateStr: string = parsedEndtDate ? DateService.dateToUkShortDate(parsedEndtDate) : ''
    return (
        <div className={styles.dateRangeContainer}>
            <div className={styles.dateRangeText}>{startDateStr} - {endDateStr}</div>
            <div className={styles.dateRangeBarsContainer}>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
                <div className={styles.dateRangeSmallBar}></div>
            </div>
        </div>
    );
};

export const renderActiveTasks = (activeTasks: string, criticalTasks: string): JSX.Element | null => {
    // Return nothing if active tasks are not valid
    if (!activeTasks || activeTasks === '0/0') return null;

    // Check if critical tasks are valid
    const showCritical = !!criticalTasks && criticalTasks !== '0/0';

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{activeTasks}</span>
            {showCritical && (
                <span
                    style={{
                        backgroundColor: 'red',
                        color: 'white',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        textAlign: 'center',
                    }}
                >
                    {criticalTasks}
                </span>
            )}
        </div>
    );
};


export const renderWithBackgroundColor = (contents: string, backgroundColor: string, textColor: string): JSX.Element => {
    if (contents == null || contents == undefined || contents === '0/0')
        return null;

    return (
        <div style={{ backgroundColor: backgroundColor, color: textColor, textAlign: 'center' }}>{contents}</div>
    );
};

export const renderTaskPriority = (priority: string, priorityColor: string): JSX.Element => {
    if (priority == null || priority == undefined)
        return null;

    return (
        <div className={styles.stageContainer}>
            <div className={styles.stageText}>{priority}</div>
            <div className={`${styles.stageBottomBar}`} style={{ backgroundColor: priorityColor }} ></div>
        </div>
    );
};

export const renderAsLink = (ID: number, title: string, onTitleClick: (ID: number) => void): JSX.Element => {
    if (ID == null || title == undefined)
        return null;

    return (
        <span>
            <a
                className="titleLnk"
                onClick={(ev) => onTitleClick(ID)}
            >
                {title}
            </a>
        </span>
    );
};
